import clsx from 'clsx'
import type {
  ChangeEvent,
  ChangeEventHandler,
  MouseEventHandler,
  MutableRefObject,
} from 'react'
import React, { useEffect, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Checkbox from '../Checkbox/Checkbox'
import { useInitialRender } from '../hooks/useInitialRender'
import Add from '../icons/Add'
import ArrowBack from '../icons/ArrowBack'
import ArrowDownward from '../icons/ArrowDownward'
import ArrowForward from '../icons/ArrowForward'
import ArrowUpward from '../icons/ArrowUpward'
import ExpandLess from '../icons/ExpandLess'
import ExpandMore from '../icons/ExpandMore'
import FilterList from '../icons/FilterList'
import VisibilityOff from '../icons/VisibilityOff'
import Menu from '../Menu/Menu'
import Typography from '../Typography/Typography'
import { Directions } from '../utilities/directions'
import { ActionTypes } from './Actions'
import ColumnHeader from './ColumnHeader'
import type { TableColumn } from './Table'
import { useTable } from './Table'

interface AddColumnProps {
  actions: HeadActions
  focusRef?: MutableRefObject<HTMLTableHeaderCellElement>
}

type HeadActions = {
  addColumn: MouseEventHandler<HTMLButtonElement>
  filterChange?: ChangeEventHandler<HTMLInputElement>
  hide: MouseEventHandler<HTMLButtonElement>
  moveLeft: MouseEventHandler<HTMLButtonElement>
  moveRight: MouseEventHandler<HTMLButtonElement>
  sortAscending: MouseEventHandler<HTMLButtonElement>
  sortDescending: MouseEventHandler<HTMLButtonElement>
}

interface MenuHeaderProps {
  column: TableColumn
  show?: boolean
}

function AddColumn({ actions, focusRef }: AddColumnProps): JSX.Element {
  const intl = useIntl()
  const [state] = useTable()
  const fallback = useRef<TableColumn>(state.columns.selected[0])
  const ref = useRef({
    focus: (options?: FocusOptions) => {
      if (focusRef?.current) {
        focusRef.current.focus(options)
      } else {
        const header = document.querySelector(
          `th[data-key="${fallback.current.key}"]`,
        ) as HTMLTableHeaderCellElement
        const row = header.parentElement as HTMLTableRowElement
        const table = row.parentElement?.parentElement as HTMLTableElement

        const cells = table.rows[row.rowIndex].cells

        cells[cells.length - 1].focus()
      }
    },
  })

  useEffect(() => {
    fallback.current = state.columns.selected[0]
  }, [state.columns.selected])

  return (
    <Menu focusRef={ref} placement="bottom-end">
      <Menu.Button
        disabled={state.columns.unselected.length === 0}
        icon
        label={intl.formatMessage({ id: 'Table.add_column' })}
        tabIndex={-1}
      >
        <Add />
      </Menu.Button>
      <Menu.Items>
        <Menu.Group label={intl.formatMessage({ id: 'Table.add_column' })}>
          {state.columns.unselected.map((column) => (
            <Menu.Item
              data-key={column.key}
              key={column.key}
              onClick={actions.addColumn}
            >
              {column.value}
            </Menu.Item>
          ))}
        </Menu.Group>
      </Menu.Items>
    </Menu>
  )
}

function Head(): JSX.Element {
  const intl = useIntl()
  const isInitial = useInitialRender()
  const [state, dispatch] = useTable()

  const actions: HeadActions = {
    addColumn: (event) => {
      dispatch({
        type: ActionTypes.ToggleColumn,
        key: event.currentTarget.dataset.key as string,
      })
    },

    hide: (event) => {
      dispatch({
        type: ActionTypes.ToggleColumn,
        key: event.currentTarget.dataset.key as string,
      })
    },

    filterChange: (event) => {
      dispatch({
        checked: event.currentTarget.checked,
        key: event.currentTarget.dataset.key as string,
        type: ActionTypes.FilterChange,
        value: event.currentTarget.name,
      })
    },

    moveLeft: (event) => {
      dispatch({
        type: ActionTypes.MoveColumn,
        direction: Directions.Left,
        key: event.currentTarget.dataset.key as string,
      })
    },

    moveRight: (event) => {
      dispatch({
        type: ActionTypes.MoveColumn,
        direction: Directions.Right,
        key: event.currentTarget.dataset.key as string,
      })
    },

    sortAscending: (event) => {
      dispatch({
        type: ActionTypes.SortColumn,
        direction: Directions.Ascending,
        key: event.currentTarget.dataset.key as string,
      })
    },

    sortDescending: (event) => {
      dispatch({
        type: ActionTypes.SortColumn,
        direction: Directions.Descending,
        key: event.currentTarget.dataset.key as string,
      })
    },
  }

  if (state.selectable) {
    const checked = state.rows.filter((row) => row?.selected)

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const status = event.target.checked ? 'checked' : 'unchecked'
      dispatch({
        type: ActionTypes.ToggleRows,
        status,
        selectionsRef: state.selectionsRef,
      })
    }

    const checkbox = {
      key: 'checkbox',
      selected: true,
      value: (
        <Checkbox.Input
          aria-label={intl.formatMessage({ id: 'Table.select_all' })}
          defaultChecked={
            checked.length === state.rows.length && state.rows.length > 0
          }
          indeterminate={
            checked.length > 0 && checked.length < state.rows.length
          }
          onChange={handleChange}
          tabIndex={-1}
        />
      ),
    }

    if (isInitial) {
      state.columns.selected = [checkbox, ...state.columns.selected]
    } else {
      const index = state.columns.selected.findIndex(
        (column) => column.key === 'checkbox',
      )

      if (index === -1) {
        state.columns.selected = [checkbox, ...state.columns.selected]
      } else {
        const columns = state.columns.selected.slice(0)

        columns[0] = checkbox

        state.columns.selected = columns
      }
    }
  }

  return (
    <thead
      className={clsx(
        'sticky top-0 z-10 transition-shadow duration-150 ease-in',
        state.isScrolling ? 'shadow-3' : 'shadow-0',
      )}
    >
      {state.loading ? null : (
        <tr>
          {state.columns.selected.map((column, index, columns) => (
            <ColumnHeader
              data-key={column.key}
              dense={column.key === 'checkbox'}
              key={column.key}
              sort={column.sort}
              tabIndex={index === 0 ? 0 : -1}
            >
              {column.key === 'checkbox' ? (
                column.value
              ) : (
                <Menu headless>
                  <Menu.Button tabIndex={-1}>
                    <MenuHeader column={column} />
                  </Menu.Button>
                  <Menu.Items>
                    {column.filter ? (
                      <Menu.Group>
                        <Menu.Item
                          data-key={column.key}
                          onClick={actions.sortAscending}
                          startAdornment={FilterList}
                        >
                          <FormattedMessage id="Table.filter" />
                          <Menu>
                            <Menu.Items>
                              <Menu.Group>
                                {column.filter.map((filter) => (
                                  <Menu.Item
                                    checkbox
                                    data-key={column.key}
                                    defaultChecked={filter.checked}
                                    key={filter.value}
                                    onChange={actions.filterChange}
                                    value={filter.value}
                                  >
                                    {filter.label}
                                  </Menu.Item>
                                ))}
                              </Menu.Group>
                            </Menu.Items>
                          </Menu>
                        </Menu.Item>
                      </Menu.Group>
                    ) : null}
                    <Menu.Group>
                      <Menu.Item
                        data-key={column.key}
                        disabled={column.sortable === false}
                        onClick={actions.sortAscending}
                        startAdornment={ArrowUpward}
                      >
                        <FormattedMessage id="Table.sort_ascending" />
                      </Menu.Item>
                      <Menu.Item
                        data-key={column.key}
                        disabled={column.sortable === false}
                        onClick={actions.sortDescending}
                        startAdornment={ArrowDownward}
                      >
                        <FormattedMessage id="Table.sort_descending" />
                      </Menu.Item>
                      <Menu.Item
                        data-key={column.key}
                        disabled={state.selectable ? index === 1 : index === 0}
                        onClick={actions.moveLeft}
                        startAdornment={ArrowBack}
                      >
                        <FormattedMessage id="Table.move_left" />
                      </Menu.Item>
                      <Menu.Item
                        data-key={column.key}
                        disabled={index === columns.length - 1}
                        onClick={actions.moveRight}
                        startAdornment={ArrowForward}
                      >
                        <FormattedMessage id="Table.move_right" />
                      </Menu.Item>
                      <Menu.Item
                        data-key={column.key}
                        disabled={
                          columns.length === 1 ||
                          (state.selectable && columns.length === 2)
                        }
                        onClick={actions.hide}
                        startAdornment={VisibilityOff}
                      >
                        <FormattedMessage id="Table.hide" />
                      </Menu.Item>
                    </Menu.Group>
                  </Menu.Items>
                </Menu>
              )}
            </ColumnHeader>
          ))}
          <ColumnHeader dense sticky>
            <AddColumn actions={actions} />
          </ColumnHeader>
        </tr>
      )}
    </thead>
  )
}

function MenuHeader({ column, show = false }: MenuHeaderProps): JSX.Element {
  return (
    <>
      <Typography variant="button">{column.value}</Typography>
      <div className="w-6 ml-2 text-black-45">
        {show ? <ExpandLess /> : <ExpandMore />}
      </div>
      {column.sort !== undefined ? (
        <div className="w-4.5 ml-auto text-black-30">
          <ArrowDownward />
        </div>
      ) : null}
    </>
  )
}

export default Head
